import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Heart, Home, Users, Gamepad2, Shield, Clock, CheckCircle, Star, ArrowRight, Download } from "lucide-react";

const Index = () => {
  const benefits = [
    {
      icon: <Heart className="h-8 w-8 text-red-500" />,
      title: "Health Management & Monitoring",
      description: "AI-powered devices can track your vital signs, remind you to take medications, and even detect falls automatically.",
      examples: ["Smart watches that monitor heart rate and blood pressure", "Medication reminder apps", "Fall detection systems"]
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Staying Connected with Family",
      description: "Voice assistants and smart devices make it easier than ever to video call loved ones and share photos.",
      examples: ["Voice-activated video calls", "Automatic photo sharing", "Simple messaging systems"]
    },
    {
      icon: <Home className="h-8 w-8 text-green-500" />,
      title: "Home Automation & Assistance",
      description: "Control lights, temperature, and security with simple voice commands - no complicated buttons or menus.",
      examples: ["Voice-controlled lighting", "Smart thermostats", "Automated door locks"]
    },
    {
      icon: <Gamepad2 className="h-8 w-8 text-purple-500" />,
      title: "Entertainment & Hobbies",
      description: "AI can suggest new activities, play your favorite music, and help you explore new interests.",
      examples: ["Personalized music recommendations", "Interactive storytelling", "Virtual hobby groups"]
    }
  ];

  const testimonials = [
    {
      name: "Margaret Thompson",
      age: 72,
      quote: "I was skeptical at first, but my voice assistant has become like a helpful friend. I can call my grandchildren just by saying 'Call Emma' - no fumbling with tiny phone buttons!",
      benefit: "Family Connection"
    },
    {
      name: "Robert Chen",
      age: 68,
      quote: "My smart watch caught an irregular heartbeat that I didn't even notice. The doctor said catching it early made all the difference. This technology literally saved my life.",
      benefit: "Health Monitoring"
    },
    {
      name: "Dorothy Williams",
      age: 75,
      quote: "I love that I can control all my lights and the thermostat just by talking. When my arthritis flares up, I don't have to get up to adjust anything.",
      benefit: "Home Automation"
    }
  ];

  const tutorials = [
    {
      title: "Getting Started with Voice Assistants",
      steps: [
        "Choose a simple device like Amazon Echo Dot or Google Nest Mini",
        "Ask a family member to help with initial setup",
        "Start with basic commands: 'What's the weather?' or 'Play some music'",
        "Practice calling family members by voice",
        "Gradually add more features as you get comfortable"
      ]
    },
    {
      title: "Setting Up Health Monitoring",
      steps: [
        "Consider a simple smartwatch designed for seniors",
        "Set up medication reminders on your phone or tablet",
        "Learn to check your heart rate and steps",
        "Share health data with your doctor if desired",
        "Set up emergency contacts for fall detection"
      ]
    },
    {
      title: "Making Your Home Smarter",
      steps: [
        "Start with smart light bulbs - they're easy to install",
        "Add a smart thermostat for comfort and energy savings",
        "Consider smart door locks for security and convenience",
        "Use voice commands to control everything hands-free",
        "Ask family to help set up routines for daily activities"
      ]
    }
  ];

  const faqs = [
    {
      question: "Is AI technology safe for seniors to use?",
      answer: "Yes, when used properly. Modern AI devices have built-in privacy protections, and you can control what information you share. Start with basic features and gradually explore more as you become comfortable."
    },
    {
      question: "Will AI technology be too complicated for me to learn?",
      answer: "Not at all! Today's AI is designed to be simple and intuitive. Voice assistants understand natural speech, and many devices work with simple voice commands. You don't need to be tech-savvy to benefit from AI."
    },
    {
      question: "How much does AI technology cost?",
      answer: "AI devices range from $30 for basic voice assistants to $200 for advanced health monitors. Many provide excellent value by improving safety, health monitoring, and quality of life. Start small and add devices as you see their benefits."
    },
    {
      question: "What if I need help setting up or using AI devices?",
      answer: "Most devices come with simple setup guides, and family members are usually happy to help. Many senior centers also offer technology classes. Customer support is available for most products, and online tutorials can guide you step-by-step."
    },
    {
      question: "Can AI really help with my health and safety?",
      answer: "Absolutely! AI can monitor vital signs, remind you about medications, detect falls, and even alert emergency contacts if needed. Many seniors report feeling safer and more independent with these technologies."
    },
    {
      question: "Will AI replace human interaction?",
      answer: "No, AI is designed to enhance, not replace, human connections. It can actually help you stay more connected with family and friends through easier communication tools and by maintaining your independence longer."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <section className="py-20 px-4 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            AI: Your New Best Friend in Retirement
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 leading-relaxed">
            Discover how artificial intelligence can make your daily life easier, safer, and more enjoyable. 
            You don't need to be a tech expert - AI is designed to work for you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button size="lg" className="text-lg px-8 py-4">
              Learn How AI Can Help You <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button variant="outline" size="lg" className="text-lg px-8 py-4">
              <Download className="mr-2 h-5 w-5" />
              Download Free Guide
            </Button>
          </div>
        </div>
      </section>

      {/* Introduction with Image */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                You're Never Too Old to Benefit from AI
              </h2>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                Artificial Intelligence isn't just for young people or tech experts. It's a powerful tool 
                that can help you live more independently, stay healthier, and remain connected with the 
                people you love.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Think of AI as a helpful assistant that never gets tired, never forgets, and is always 
                ready to lend a hand - whether that's reminding you to take your medication, helping 
                you call your grandchildren, or making sure your home is comfortable and secure.
              </p>
            </div>
            <div className="text-center">
              <img 
                src="/images/senior_using_technology_1.jpeg" 
                alt="Happy senior woman using tablet technology"
                className="rounded-lg shadow-lg w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            How AI Can Improve Your Daily Life
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="h-full hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-4 mb-4">
                    {benefit.icon}
                    <CardTitle className="text-2xl">{benefit.title}</CardTitle>
                  </div>
                  <CardDescription className="text-lg leading-relaxed">
                    {benefit.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <h4 className="font-semibold text-lg mb-3">Real Examples:</h4>
                  <ul className="space-y-2">
                    {benefit.examples.map((example, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-lg">{example}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Real Stories from People Like You
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardDescription className="text-lg italic leading-relaxed">
                    "{testimonial.quote}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-lg">{testimonial.name}</p>
                      <p className="text-gray-600">Age {testimonial.age}</p>
                    </div>
                    <Badge variant="secondary" className="text-sm">
                      {testimonial.benefit}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Examples Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            See AI in Action
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Health Monitoring Made Simple</h3>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                Modern smartwatches can track your heart rate, count your steps, and even detect if you've fallen. 
                They can alert family members or emergency services if needed, giving you and your loved ones peace of mind.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Automatic heart rate monitoring
                </li>
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Fall detection with emergency alerts
                </li>
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Medication reminders
                </li>
              </ul>
            </div>
            <div className="text-center">
              <img 
                src="/images/ai_health_monitoring_2.webp" 
                alt="Senior using smartwatch for health monitoring"
                className="rounded-lg shadow-lg w-full max-w-md mx-auto"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:order-1">
              <img 
                src="/images/smart_home_seniors_2.jpeg" 
                alt="Senior couple using voice assistant at home"
                className="rounded-lg shadow-lg w-full max-w-md mx-auto"
              />
            </div>
            <div className="md:order-2">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Smart Home, Simple Life</h3>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                Voice assistants like Alexa or Google Home can control your lights, adjust the temperature, 
                and even help you make phone calls - all without touching a single button.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Voice-controlled lighting and temperature
                </li>
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Hands-free phone calls and messaging
                </li>
                <li className="flex items-center gap-3 text-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  Music and entertainment on demand
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-Step Tutorials */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Easy Step-by-Step Guides
          </h2>
          <div className="space-y-8">
            {tutorials.map((tutorial, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <Badge variant="outline" className="text-lg px-3 py-1">
                      {index + 1}
                    </Badge>
                    {tutorial.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-4">
                    {tutorial.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex items-start gap-4">
                        <div className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center font-semibold flex-shrink-0 mt-1">
                          {stepIndex + 1}
                        </div>
                        <p className="text-lg text-gray-700 leading-relaxed">{step}</p>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Common Questions & Concerns
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg px-6">
                <AccordionTrigger className="text-xl font-semibold text-left py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-lg text-gray-700 leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Make Your Life Easier?
          </h2>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed">
            You don't have to navigate this alone. Start small, ask for help when you need it, 
            and discover how AI can enhance your independence and quality of life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
              <Download className="mr-2 h-5 w-5" />
              Get Your Free Starter Guide
            </Button>
            <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
              Find Local Tech Support
            </Button>
          </div>
          <p className="mt-8 text-lg opacity-90">
            Remember: Every expert was once a beginner. You've got this!
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">AI for Retirees</h3>
          <p className="text-lg text-gray-300 mb-6">
            Empowering seniors to embrace technology for a better, more connected life.
          </p>
          <div className="flex justify-center items-center gap-2 text-gray-400">
            <Shield className="h-5 w-5" />
            <span>Safe • Simple • Supportive</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;